'use client';

import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { DialogTitle } from '../../ui/dialog';
import { ModalCloseButton } from '../../common/modal-close-button';
import Dropdown from '../../common/dropdown';
import { registerLocal, authErrorMessage } from '@/lib/auth/common/client';
import { finalizeClientLogin } from '@/lib/auth/common/finalize-login';
import { normalizeEmail } from '@/utils/normalize-email';
import { useLoginModals } from '../common/LoginModalsManager';
import { sanitizeUserInput, sanitizeEmail } from '@/utils/sanitize-input';
import { containsXss, validatePasswordStrength, isValidEmail } from '@/utils/validate-input';
import MaterialIcons from '@/components/MaterialIcons/materialIcons';
import { PasswordField } from './PasswordField';

interface EmailPasswordRegistrationModalProps {
  onClose?: () => void;
  onBack?: () => void;
}

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  role: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
  role?: string;
  password?: string;
  confirmPassword?: string;
}

const roleOptions = [
  { value: 'developer', label: 'Developer' },
  { value: 'contentManager', label: 'Content Manager' },
  { value: 'leader', label: 'Leader' },
];

export function EmailPasswordRegistrationModal({
  onClose,
  onBack,
}: EmailPasswordRegistrationModalProps) {
  const { closeLoginModal } = useLoginModals();

  const [formData, setFormData] = useState<RegisterData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    role: '',
    password: '',
    confirmPassword: '',
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field: keyof RegisterData, value: string) => {
    // Security: Sanitize all user inputs to prevent XSS attacks
    let sanitizedValue = value;
    
    // Apply appropriate sanitization based on field type
    if (field === 'email') {
      // Convert to lowercase for consistency
      sanitizedValue = sanitizeEmail(value.toLowerCase());
    } else if (field === 'firstName' || field === 'lastName' || field === 'company') {
      sanitizedValue = sanitizeUserInput(value);
    }
    // Password fields are not sanitized to preserve special characters, but checked for XSS
    else if (field === 'password' || field === 'confirmPassword') {
      // Block if contains malicious XSS patterns
      if (containsXss(value)) {
        return; // Don't update state if XSS detected
      }
      sanitizedValue = value;
    }
    
    setFormData((prev) => ({ ...prev, [field]: sanitizedValue }));
    // Clear error for this field when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    setErrorMsg('');
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // First name validation
    if (!formData.firstName) {
      newErrors.firstName = 'First name is required';
    } else if (!/^[a-zA-Z\u00C0-\u017F\s'\-\.'']*$/.test(formData.firstName)) {
      newErrors.firstName =
        'First name can only contain letters, spaces, apostrophes, hyphens, and periods';
    } else if (formData.firstName.length > 25) {
      newErrors.firstName = 'First name cannot be more than 25 characters';
    }

    // Last name validation
    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required';
    } else if (!/^[a-zA-Z\u00C0-\u017F\s'\-\.'']*$/.test(formData.lastName)) {
      newErrors.lastName =
        'Last name can only contain letters, spaces, apostrophes, hyphens, and periods';
    } else if (formData.lastName.length > 25) {
      newErrors.lastName = 'Last name cannot be more than 25 characters';
    }

    // Email validation
    if (!formData.email || !isValidEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Company validation
    if (!formData.company) {
      newErrors.company = 'Company name is required';
    } else if (
      !/^[a-zA-Z0-9\u00C0-\u017F\s'\-\.''\+\@\,\&\(\)]*$/.test(formData.company)
    ) {
      newErrors.company =
        'Company name can only contain letters, spaces, apostrophes, hyphens, periods, commas, ampersands, and parentheses';
    } else if (formData.company.length > 50) {
      newErrors.company = 'Company name cannot be more than 50 characters';
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    // Password validation - Enhanced with security checks
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else {
      const passwordValidation = validatePasswordStrength(formData.password);
      if (!passwordValidation.isValid) {
        // Show the first error message from validation
        newErrors.password = passwordValidation.messages[0] || 'Password is too weak';
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    return true;
  };

  const handleRegister = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrorMsg('');

    try {
      const normalizedEmail = normalizeEmail(formData.email);

      const { status, body } = await registerLocal({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: normalizedEmail,
        role: formData.role,
        company: formData.company,
        password: formData.password,
      });

      if (status === 201) {
        // Persist the user and notify listeners.
        finalizeClientLogin(body);

        // Close modal
        closeLoginModal();
        onClose?.();

        // Full reload, same as email/password login and logout — see the comment
        // on useLoginForm's handleLogin for why this replaced the soft
        // router.refresh() + React Query reactivity approach.
        window.location.reload();
      } else {
        setLoading(false);

        const message = authErrorMessage(body, 'Registration failed. Please try again.');
        if (message.includes('already exists')) {
          setErrors({ email: 'User with email already exists' });
        } else {
          setErrorMsg(message);
        }
      }
    } catch {
      setLoading(false);
      setErrorMsg('Registration failed. Please try again.');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRegister();
    }
  };

  return (
    <div className="login-modal-container !w-full mx-auto sm:!max-w-[550px] md:!max-w-[700px] lg:!max-w-[960px]">
      <ModalCloseButton onClick={onClose} />
      <div className="relative z-10">
        {/* Header */}
        <div className="mb-6">
          <DialogTitle className="cs-h3 tab:h1-compact">
            Let&apos;s set up your Contentstack Academy account
          </DialogTitle>
        </div>

        {/* Form */}
        <div className="flex flex-col gap-4 lg:gap-8">
          {/* First Name and Last Name Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="firstName"
                className="cs-input-field flex items-center justify-between"
              >
                <span>First name</span>
                <span className="cs-asterisk">*</span>
              </label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                onFocus={() => setErrors((prev) => ({ ...prev, firstName: undefined }))}
                onKeyUp={(e) => handleKeyPress(e)}
                className={`min-h-[40px] touch-manipulation ${
                  errors.firstName ? 'border-destructive cs-input-ring-error' : ''
                }`}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="lastName"
                className="cs-input-field flex items-center justify-between"
              >
                <span>Last name</span>
                <span className="cs-asterisk">*</span>
              </label>
              <Input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                onFocus={() => setErrors((prev) => ({ ...prev, lastName: undefined }))}
                onKeyUp={(e) => handleKeyPress(e)}
                className={`min-h-[40px] !bg-surface-3 touch-manipulation ${
                  errors.lastName ? 'border-destructive cs-input-ring-error' : ''
                }`}
              />
            </div>
          </div>

          {/* Email and Company Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="email"
                className="cs-input-field flex items-center justify-between"
              >
                <span>Email</span>
                <span className="cs-asterisk">*</span>
              </label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value.toLowerCase())}
                onFocus={() => setErrors((prev) => ({ ...prev, email: undefined }))}
                onKeyUp={(e) => handleKeyPress(e)}
                className={`min-h-[40px] !bg-surface-3 touch-manipulation ${
                  errors.email ? 'border-destructive cs-input-ring-error' : ''
                }`}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="company"
                className="cs-input-field flex items-center justify-between"
              >
                <span>Company</span>
                <span className="cs-asterisk">*</span>
              </label>
              <Input
                id="company"
                  type="text"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                onFocus={() => setErrors((prev) => ({ ...prev, company: undefined }))}
                onKeyUp={(e) => handleKeyPress(e)}
                className={`min-h-[40px] !bg-surface-3 touch-manipulation ${
                  errors.company ? 'border-destructive cs-input-ring-error' : ''
                }`}
              />
            </div>
          </div>

          {/* Password and Confirm Password Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="password"
                className="cs-input-field flex items-center justify-between"
              >
                <span>Password</span>
                <span className="cs-asterisk">*</span>
              </label>
              <PasswordField
                id="password"
                value={formData.password}
                onChange={(value) => handleInputChange('password', value)}
                onFocus={() => setErrors((prev) => ({ ...prev, password: undefined }))}
                onKeyUp={handleKeyPress}
                hasError={!!errors.password}
                className="!bg-surface-3"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="confirmPassword"
                className="cs-input-field flex items-center justify-between"
              >
                <span>Confirm Password</span>
                <span className="cs-asterisk">*</span>
              </label>
              <PasswordField
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={(value) => handleInputChange('confirmPassword', value)}
                onFocus={() => setErrors((prev) => ({ ...prev, confirmPassword: undefined }))}
                onKeyUp={handleKeyPress}
                hasError={!!errors.confirmPassword}
                className="!bg-surface-3"
              />
            </div>
          </div>

          {/* Role - 2 columns out of 3 on desktop, full width on mobile/tablet */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 flex flex-col gap-2">
              <label
                htmlFor="role"
                className="cs-input-field flex items-center justify-between"
              >
                <span>Role</span>
                <span className="cs-asterisk">*</span>
              </label>
              <Dropdown
                id="role"
                options={roleOptions}
                defaultValue={formData.role || undefined}
                placeholder="- Select Role"
                onChange={(value) => {
                  handleInputChange('role', value as string);
                  setErrors((prev) => ({ ...prev, role: undefined }));
                }}
                className={`${formData.role ? 'bg-surface-5' : 'bg-surface-3'} cs-input-field hover:bg-surface-4 touch-manipulation cursor-pointer ${
                  errors.role ? 'border-destructive cs-input-ring-error' : ''
                }`}
              />
            </div>
          </div>

          {/* Buttons - Bottom Right */}
          <div className="flex flex-col items-end gap-2 mt-4 tab:mt-8">
            <div className="flex items-center justify-end gap-4">
              <Button
                onClick={() => {
                  if (onBack) {
                    onBack();
                  } else {
                    onClose?.();
                  }
                }}
                variant="SecondaryFillButton"
                className="cs-button touch-manipulation"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRegister}
                disabled={loading}
                variant="PrimaryFillButton"
                className="cs-button text-onAction-strong touch-manipulation flex items-center gap-2"
              >
                <span>{loading ? 'Activating Account...' : 'Activate Account'}</span>
                {!loading && (
                  <MaterialIcons iconName="arrow_right_alt" className="btn-icon !text-[20px] flex-shrink-0 leading-none transition-transform duration-300 ease-in-out group-hover:translate-x-1" /> 
                )}
              </Button>
            </div>
            {/* Error Message - Show below buttons, aligned to right */}
            {errorMsg || Object.keys(errors).length > 0 ? (
              <div className="w-full">
                <h6 className="cs-error cs-h6 text-right">
                  {errorMsg || Object.values(errors).find((err) => err) || ''}
                </h6>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

